import { Color3, Color4, PhysicsMotionType, PhysicsShapeType, Vector2, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { DecorationConfig } from '../DecorationGenerator';
import { EnemyConfig } from '../EnemyGenerator';
import { EnemyPrefabs } from '../EnemyPrefabs';
import { FogConfig } from '../FogConfig';
import { Level } from '../Level';
import {
  BaseSceneConfig,
  CameraConfig,
  CollectibleDistributionConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PlayerConfig,
  PostProcessConfig,
  SoundConfig,
  WaterConfig,
} from '../LevelConfig';
import { LightConfig } from '../LightConfig';
import { HeightMapConfig, SkyBoxConfig } from '../MeshFactory';
import { ObstacleConfig } from '../ObstacleGenerator';
import { ObstaclePrefabs } from '../ObstaclePrefabs';
import { TextureAtlas } from '../TextureAtlas';
import { LevelTemplate } from './LevelTemplate';

export class TemplateBaseScene implements LevelTemplate {
  create(): LevelConfig {
    const skyboxConfig: SkyBoxConfig = {
      url: 'assets/skybox/SunsetSky.png',
      rotationAxis: 'y',
      rotationSpeed: 0.000005,
    };
    const fogConfig = new FogConfig();

    const lightConfig = new LightConfig();
    lightConfig.shadowCasterDistance = 80;

    const postProcessConfig: PostProcessConfig = {
      fxaa: false,
      motionBlur: 0,
      //blur: { kernel: 32, strength: 1, x: 1, y: 0 },
      highlights: false,
      sepia: false,
      fireflies: 1,
      //birds: 20,
      //rain: 1,
      //heavyRain: 1,
      //groundFog: 1,
      //advancedRain: 0.25,
    };

    const materialConfig: MaterialConfig = {
      colorMaterialBaseColor: new Color3(0.5, 0.7, 0.9),
      colorMaterialCount: 0,
      textures: [
        { name: 'lava', url: 'assets/textures/lava_small.png', uScale: 1, vScale: 1 },
        { name: 'grass', url: 'assets/textures/realistic-grass.jpg', uScale: 1, vScale: 1 },
        { name: 'mud', url: 'assets/textures/cartoon1/Dirt.jpeg', uScale: 1, vScale: 1 },
        { name: 'cracked', url: 'assets/textures/cartoon1/Cracked.jpeg', uScale: 1, vScale: 1 },
        { name: 'stone', url: 'assets/textures/rock_seamless.jpg', uScale: 1, vScale: 1 },
      ],
      globalTextureNames: [],
      materials: [],
    };

    const pathConfig: PathConfig = {
      start: new Vector3(0, 0, 0),
      destination: new Vector3(0, 0, 500),
      pathPoints: 10,
      segmentsPerCurve: 15,
      pathCurvature: 1.5,
      pathHilliness: 0.5,
      pathScale: 3.0,
      pathMinCurveAngle: 90,
      laneWidth: 3,
      spaceAround: 50,
      pathBoundary: 0,
    };

    pathConfig.predefined = [
      new Vector3(64.985, 1.859, 85.879),
      new Vector3(66.787, 1.86, 85.597),
      new Vector3(69.399, 1.86, 85.221),
      new Vector3(72.152, 1.861, 84.616),
      new Vector3(74.377, 1.861, 83.645),
      new Vector3(75.946, 1.861, 82.203),
      new Vector3(77.199, 1.86, 80.384),
      new Vector3(78.17, 1.86, 78.358),
      new Vector3(78.891, 1.859, 76.292),
      new Vector3(79.276, 1.859, 74.193),
      new Vector3(79.34, 1.859, 71.984),
      new Vector3(79.266, 1.859, 69.707),
      new Vector3(79.239, 1.859, 67.406),
      new Vector3(79.29, 1.859, 65.061),
      new Vector3(79.327, 1.859, 62.659),
      new Vector3(79.349, 1.859, 60.255),
      new Vector3(79.356, 1.859, 57.905),
      new Vector3(79.343, 1.859, 55.654),
      new Vector3(79.312, 1.859, 53.462),
      new Vector3(79.272, 1.859, 51.257),
      new Vector3(79.234, 1.859, 48.968),
      new Vector3(79.198, 1.859, 46.532),
      new Vector3(79.159, 1.859, 44.001),
      new Vector3(79.12, 1.859, 41.477),
      new Vector3(79.083, 1.859, 39.063),
      new Vector3(79.044, 1.859, 36.819),
      new Vector3(79.003, 1.859, 34.68),
      new Vector3(78.971, 1.859, 32.557),
      new Vector3(78.961, 1.859, 30.361),
      new Vector3(78.988, 1.859, 28.034),
      new Vector3(79.041, 1.859, 25.635),
      new Vector3(79.093, 1.859, 23.252),
      new Vector3(79.115, 1.859, 20.972),
      new Vector3(79.078, 1.75, 18.779),
      new Vector3(79.003, 1.568, 16.638),
      new Vector3(78.94, 1.532, 14.615),
      new Vector3(78.937, 1.859, 12.777),
      new Vector3(79.132, 2.915, 11.337),
      new Vector3(79.459, 4.48, 10.195),
      new Vector3(79.655, 5.9, 8.943),
      new Vector3(79.459, 6.519, 7.175),
      new Vector3(78.952, 5.9, 4.357),
      new Vector3(78.224, 4.48, 0.847),
      new Vector3(77.022, 2.915, -2.423),
      new Vector3(75.093, 1.859, -4.52),
      new Vector3(72.026, 1.532, -4.858),
      new Vector3(68.072, 1.568, -4.046),
      new Vector3(63.965, 1.75, -2.948),
      new Vector3(60.44, 1.859, -2.427),
      new Vector3(57.637, 1.859, -2.529),
      new Vector3(55.183, 1.859, -2.856),
      new Vector3(53.042, 1.859, -3.601),
      new Vector3(51.177, 1.859, -4.956),
      new Vector3(49.587, 1.859, -7.209),
      new Vector3(48.289, 1.859, -10.176),
      new Vector3(47.273, 1.859, -13.353),
      new Vector3(46.528, 1.859, -16.232),
      new Vector3(46.143, 1.859, -18.675),
      new Vector3(46.092, 1.859, -20.953),
      new Vector3(46.198, 1.859, -23.175),
      new Vector3(46.284, 1.859, -25.449),
      new Vector3(46.353, 1.853, -27.819),
      new Vector3(46.482, 1.843, -30.222),
      new Vector3(46.608, 1.841, -32.609),
      new Vector3(46.667, 1.859, -34.929),
      new Vector3(46.622, 1.912, -37.133),
      new Vector3(46.514, 1.99, -39.258),
      new Vector3(46.393, 2.067, -41.391),
      new Vector3(46.311, 2.12, -43.617),
      new Vector3(46.279, 2.145, -45.978),
      new Vector3(46.271, 2.153, -48.422),
      new Vector3(46.279, 2.145, -50.892),
      new Vector3(46.295, 2.12, -53.333),
      new Vector3(46.324, 2.067, -55.739),
      new Vector3(46.369, 1.988, -58.137),
      new Vector3(46.416, 1.91, -60.518),
      new Vector3(46.452, 1.859, -62.873),
      new Vector3(46.525, 1.843, -65.196),
      new Vector3(46.625, 1.847, -67.494),
      new Vector3(46.65, 1.865, -69.776),
      new Vector3(46.499, 1.895, -72.053),
      new Vector3(46.205, 1.949, -74.31),
      new Vector3(45.801, 2.026, -76.549),
      new Vector3(45.189, 2.094, -78.801),
      new Vector3(44.272, 2.12, -81.098),
      new Vector3(42.905, 2.083, -83.46),
      new Vector3(41.182, 2.004, -85.866),
      new Vector3(39.353, 1.918, -88.288),
      new Vector3(37.672, 1.859, -90.696),
      new Vector3(36.288, 1.841, -93.259),
      new Vector3(35.038, 1.843, -95.934),
      new Vector3(33.709, 1.853, -98.375),
      new Vector3(32.091, 1.859, -100.238),
      new Vector3(30.062, 1.859, -101.339),
      new Vector3(27.758, 1.859, -101.894),
      new Vector3(25.346, 1.859, -102.151),
      new Vector3(22.992, 1.859, -102.358),
      new Vector3(20.733, 1.858, -102.499),
      new Vector3(18.481, 1.854, -102.471),
      new Vector3(16.224, 1.854, -102.385),
      new Vector3(13.946, 1.86, -102.355),
      new Vector3(11.643, 1.879, -102.42),
      new Vector3(9.322, 1.906, -102.515),
      new Vector3(6.989, 1.931, -102.604),
      new Vector3(4.649, 1.942, -102.651),
      new Vector3(2.302, 1.931, -102.642),
      new Vector3(-0.054, 1.906, -102.597),
      new Vector3(-2.42, 1.878, -102.534),
      new Vector3(-4.799, 1.859, -102.468),
      new Vector3(-7.132, 1.854, -102.385),
      new Vector3(-9.438, 1.854, -102.283),
      new Vector3(-11.834, 1.857, -102.197),
      new Vector3(-14.441, 1.859, -102.164),
      new Vector3(-17.415, 1.859, -102.336),
      new Vector3(-20.651, 1.859, -102.648),
      new Vector3(-23.872, 1.859, -102.805),
      new Vector3(-26.802, 1.859, -102.515),
      new Vector3(-29.422, 1.859, -101.737),
      new Vector3(-31.861, 1.859, -100.614),
      new Vector3(-34.064, 1.859, -99.132),
      new Vector3(-35.974, 1.859, -97.273),
      new Vector3(-37.507, 1.853, -94.847),
      new Vector3(-38.716, 1.843, -91.924),
      new Vector3(-39.752, 1.841, -88.882),
      new Vector3(-40.765, 1.859, -86.098),
      new Vector3(-41.838, 1.919, -83.698),
      new Vector3(-42.877, 2.006, -81.473),
      new Vector3(-43.767, 2.086, -79.295),
      new Vector3(-44.392, 2.12, -77.037),
      new Vector3(-44.65, 2.086, -74.645),
      new Vector3(-44.626, 2.006, -72.192),
      new Vector3(-44.489, 1.919, -69.748),
      new Vector3(-44.408, 1.859, -67.382),
      new Vector3(-44.41, 1.835, -65.146),
      new Vector3(-44.411, 1.827, -62.992),
      new Vector3(-44.413, 1.835, -60.837),
      new Vector3(-44.418, 1.859, -58.596),
      new Vector3(-44.424, 1.919, -56.235),
      new Vector3(-44.43, 2.006, -53.802),
      new Vector3(-44.44, 2.086, -51.34),
      new Vector3(-44.458, 2.12, -48.889),
      new Vector3(-44.496, 2.086, -46.434),
      new Vector3(-44.548, 2.006, -43.964),
      new Vector3(-44.591, 1.919, -41.519),
      new Vector3(-44.6, 1.859, -39.144),
      new Vector3(-44.55, 1.841, -36.872),
      new Vector3(-44.459, 1.843, -34.673),
      new Vector3(-44.374, 1.853, -32.493),
      new Vector3(-44.339, 1.859, -30.279),
      new Vector3(-44.239, 1.859, -27.586),
      new Vector3(-44.092, 1.859, -24.588),
      new Vector3(-44.146, 1.859, -22.153),
      new Vector3(-44.65, 1.859, -21.152),
      new Vector3(-45.812, 1.859, -22.175),
      new Vector3(-47.452, 1.859, -24.637),
      new Vector3(-49.237, 1.858, -27.653),
      new Vector3(-50.834, 1.859, -30.333),
      new Vector3(-52.054, 1.863, -32.621),
      new Vector3(-53.109, 1.868, -34.93),
      new Vector3(-54.264, 1.872, -37.081),
      new Vector3(-55.783, 1.874, -38.894),
      new Vector3(-57.802, 1.872, -40.437),
      new Vector3(-60.158, 1.868, -41.768),
      new Vector3(-62.668, 1.863, -42.691),
      new Vector3(-65.152, 1.859, -43.011),
      new Vector3(-67.664, 1.852, -42.599),
      new Vector3(-70.266, 1.842, -41.583),
      new Vector3(-72.787, 1.841, -40.159),
      new Vector3(-75.052, 1.859, -38.522),
      new Vector3(-77.008, 1.918, -36.593),
      new Vector3(-78.751, 2.005, -34.312),
      new Vector3(-80.327, 2.084, -31.9),
      new Vector3(-81.784, 2.12, -29.58),
      new Vector3(-83.247, 2.083, -27.41),
      new Vector3(-84.653, 1.997, -25.272),
      new Vector3(-85.768, 1.913, -23.122),
      new Vector3(-86.355, 1.88, -20.918),
      new Vector3(-86.325, 1.927, -18.545),
      new Vector3(-85.811, 2.023, -16.061),
      new Vector3(-84.919, 2.125, -13.68),
      new Vector3(-83.75, 2.189, -11.621),
      new Vector3(-82.292, 2.203, -9.983),
      new Vector3(-80.503, 2.19, -8.636),
      new Vector3(-78.446, 2.16, -7.453),
      new Vector3(-76.184, 2.12, -6.309),
      new Vector3(-73.592, 2.056, -5.117),
      new Vector3(-70.683, 1.969, -3.96),
      new Vector3(-67.728, 1.892, -2.973),
      new Vector3(-64.997, 1.859, -2.286),
      new Vector3(-62.594, 1.9, -1.915),
      new Vector3(-60.364, 1.99, -1.795),
      new Vector3(-58.188, 2.08, -1.943),
      new Vector3(-55.946, 2.12, -2.372),
      new Vector3(-53.609, 2.086, -3.515),
      new Vector3(-51.239, 2.006, -5.221),
      new Vector3(-48.858, 1.919, -6.631),
      new Vector3(-46.488, 1.859, -6.885),
      new Vector3(-43.985, 1.841, -5.51),
      new Vector3(-41.386, 1.843, -3.045),
      new Vector3(-38.99, 1.853, -0.153),
      new Vector3(-37.095, 1.859, 2.504),
      new Vector3(-35.977, 1.859, 4.809),
      new Vector3(-35.412, 1.859, 7.093),
      new Vector3(-34.944, 1.859, 9.375),
      new Vector3(-34.116, 1.859, 11.67),
      new Vector3(-32.846, 1.859, 14.046),
      new Vector3(-31.364, 1.859, 16.474),
      new Vector3(-29.682, 1.859, 18.823),
      new Vector3(-27.814, 1.859, 20.964),
      new Vector3(-25.689, 1.859, 22.908),
      new Vector3(-23.327, 1.859, 24.709),
      new Vector3(-20.867, 1.859, 26.301),
      new Vector3(-18.453, 1.859, 27.619),
      new Vector3(-16.093, 1.859, 28.709),
      new Vector3(-13.723, 1.859, 29.584),
      new Vector3(-11.368, 1.859, 30.131),
      new Vector3(-9.054, 1.859, 30.236),
      new Vector3(-6.595, 1.853, 29.864),
      new Vector3(-4.043, 1.843, 29.078),
      new Vector3(-1.775, 1.841, 27.908),
      new Vector3(-0.17, 1.859, 26.386),
      new Vector3(0.514, 1.919, 24.357),
      new Vector3(0.532, 2.006, 21.858),
      new Vector3(0.271, 2.086, 19.209),
      new Vector3(0.12, 2.12, 16.732),
      new Vector3(0.128, 1.977, 14.422),
      new Vector3(0.108, 1.715, 12.139),
      new Vector3(0.085, 1.591, 9.996),
      new Vector3(0.086, 1.859, 8.111),
      new Vector3(0.127, 2.792, 6.659),
      new Vector3(0.19, 4.186, 5.532),
      new Vector3(0.254, 5.581, 4.412),
      new Vector3(0.295, 6.519, 2.983),
      new Vector3(0.094, 6.809, 0.931),
      new Vector3(-0.267, 6.719, -1.498),
      new Vector3(-0.353, 6.475, -3.784),
      new Vector3(0.269, 6.304, -5.405),
      new Vector3(2.067, 6.236, -5.858),
      new Vector3(4.692, 6.16, -5.542),
      new Vector3(7.353, 6.091, -5.291),
      new Vector3(9.259, 6.043, -5.94),
      new Vector3(10.129, 6.025, -7.885),
      new Vector3(10.409, 6.027, -10.623),
      new Vector3(10.395, 6.037, -13.639),
      new Vector3(10.383, 6.043, -16.419),
      new Vector3(10.599, 6.035, -19.043),
      new Vector3(10.834, 6.021, -21.713),
      new Vector3(10.74, 6.018, -24.095),
      new Vector3(9.964, 6.043, -25.853),
      new Vector3(8.016, 6.116, -26.556),
      new Vector3(5.215, 6.221, -26.498),
      new Vector3(2.426, 6.327, -26.428),
      new Vector3(0.515, 6.4, -27.097),
      new Vector3(-0.155, 6.425, -29.025),
      new Vector3(-0.088, 6.422, -31.706),
      new Vector3(0.274, 6.408, -34.347),
      new Vector3(0.491, 6.4, -36.159),
    ];

    const assetConfig = [new Asset('ground-mesh', AssetType.MESH, 'assets/model/predefined/', 'egypt_catacomb.glb')];

    const baseSceneConfig: BaseSceneConfig = {
      object: AssetObject.fromAsset('ground-mesh', 30 / 4),
      offset: new Vector3(0, 0, 0),
      exclude: [],
      disableGround: [],
      disablePhysics: [],
    };

    // const assetConfig = [new Asset('ground-mesh', AssetType.MESH, 'assets/model/predefined/', 'moonview_highway.glb')];

    // const baseSceneConfig: BaseSceneConfig = {
    //   object: AssetObject.fromAsset('ground-mesh', 200),
    //   offset: new Vector3(0, 0, 0),
    //   exclude: [
    //     'Object_16',
    //     'Object_17',
    //     'Object_18',
    //     'Object_39',
    //     'Object_40',
    //     'Object_41',
    //     'Object_12',
    //     'Object_45',
    //     'Object_2',
    //     'Object_3',
    //     'Object_54',
    //     'Object_55',
    //     'Object_54',
    //     'Object_57',
    //     'Object_0',
    //   ],
    //   disableGround: ['Object_21', 'Object_22', 'Object_23', 'Object_34', 'Object_35', 'Object_36'],
    //   disablePhysics: [],
    // };

    // pathConfig.predefined = [
    //   new Vector3(-124.731, 10.307, 7.951),
    //   new Vector3(-124.735, 10.307, 9.154),
    //   new Vector3(-124.741, 10.307, 10.697),
    //   new Vector3(-124.749, 10.307, 12.521),
    //   new Vector3(-124.757, 10.307, 14.568),
    //   new Vector3(-124.766, 10.307, 16.778),
    //   new Vector3(-124.774, 10.307, 19.092),
    //   new Vector3(-124.78, 10.307, 21.452),
    //   new Vector3(-124.784, 10.307, 23.799),
    //   new Vector3(-124.786, 10.307, 26.073),
    //   new Vector3(-124.783, 10.307, 28.217),
    //   new Vector3(-124.777, 10.302, 30.244),
    //   new Vector3(-124.769, 10.291, 32.223),
    //   new Vector3(-124.757, 10.275, 34.178),
    //   new Vector3(-124.744, 10.257, 36.132),
    //   new Vector3(-124.729, 10.242, 38.108),
    //   new Vector3(-124.713, 10.233, 40.128),
    //   new Vector3(-124.696, 10.231, 42.216),
    //   new Vector3(-124.679, 10.241, 44.396),
    //   new Vector3(-124.661, 10.265, 46.689),
    //   new Vector3(-124.644, 10.307, 49.119),
    //   new Vector3(-124.625, 10.362, 51.715),
    //   new Vector3(-124.601, 10.425, 54.47),
    //   new Vector3(-124.573, 10.497, 57.351),
    //   new Vector3(-124.544, 10.579, 60.326),
    //   new Vector3(-124.517, 10.672, 63.362),
    //   new Vector3(-124.491, 10.777, 66.428),
    //   new Vector3(-124.471, 10.896, 69.492),
    //   new Vector3(-124.456, 11.029, 72.521),
    //   new Vector3(-124.45, 11.177, 75.482),
    //   new Vector3(-124.455, 11.342, 78.345),
    //   new Vector3(-124.473, 11.531, 81.137),
    //   new Vector3(-124.505, 11.745, 83.906),
    //   new Vector3(-124.547, 11.979, 86.653),
    //   new Vector3(-124.596, 12.23, 89.376),
    //   new Vector3(-124.649, 12.491, 92.074),
    //   new Vector3(-124.702, 12.758, 94.746),
    //   new Vector3(-124.751, 13.026, 97.393),
    //   new Vector3(-124.794, 13.29, 100.012),
    //   new Vector3(-124.826, 13.544, 102.603),
    //   new Vector3(-124.844, 13.785, 105.165),
    //   new Vector3(-124.848, 14.016, 107.673),
    //   new Vector3(-124.843, 14.243, 110.114),
    //   new Vector3(-124.829, 14.468, 112.505),
    //   new Vector3(-124.81, 14.69, 114.863),
    //   new Vector3(-124.785, 14.91, 117.206),
    //   new Vector3(-124.758, 15.128, 119.551),
    //   new Vector3(-124.729, 15.344, 121.915),
    //   new Vector3(-124.701, 15.558, 124.315),
    //   new Vector3(-124.675, 15.771, 126.768),
    //   new Vector3(-124.652, 15.983, 129.293),
    //   new Vector3(-124.677, 16.199, 131.929),
    //   new Vector3(-124.77, 16.422, 134.679),
    //   new Vector3(-124.903, 16.648, 137.508),
    //   new Vector3(-125.045, 16.874, 140.38),
    //   new Vector3(-125.168, 17.095, 143.259),
    //   new Vector3(-125.241, 17.309, 146.107),
    //   new Vector3(-125.235, 17.511, 148.891),
    //   new Vector3(-125.12, 17.698, 151.572),
    //   new Vector3(-124.868, 17.866, 154.115),
    //   new Vector3(-124.447, 18.011, 156.484),
    //   new Vector3(-123.923, 18.132, 158.704),
    //   new Vector3(-123.362, 18.231, 160.827),
    //   new Vector3(-122.737, 18.313, 162.855),
    //   new Vector3(-122.022, 18.379, 164.793),
    //   new Vector3(-121.191, 18.433, 166.643),
    //   new Vector3(-120.217, 18.479, 168.409),
    //   new Vector3(-119.074, 18.518, 170.093),
    //   new Vector3(-117.734, 18.555, 171.7),
    //   new Vector3(-116.172, 18.592, 173.232),
    //   new Vector3(-114.362, 18.633, 174.693),
    //   new Vector3(-112.223, 18.674, 176.05),
    //   new Vector3(-109.742, 18.71, 177.282),
    //   new Vector3(-106.984, 18.742, 178.408),
    //   new Vector3(-104.016, 18.77, 179.448),
    //   new Vector3(-100.906, 18.795, 180.423),
    //   new Vector3(-97.721, 18.816, 181.352),
    //   new Vector3(-94.527, 18.835, 182.256),
    //   new Vector3(-91.391, 18.852, 183.155),
    //   new Vector3(-88.381, 18.866, 184.068),
    //   new Vector3(-85.563, 18.879, 185.016),
    //   new Vector3(-82.921, 18.889, 185.972),
    //   new Vector3(-80.376, 18.895, 186.9),
    //   new Vector3(-77.901, 18.897, 187.807),
    //   new Vector3(-75.471, 18.897, 188.699),
    //   new Vector3(-73.06, 18.895, 189.584),
    //   new Vector3(-70.642, 18.891, 190.468),
    //   new Vector3(-68.191, 18.887, 191.359),
    //   new Vector3(-65.681, 18.883, 192.264),
    //   new Vector3(-63.086, 18.88, 193.189),
    //   new Vector3(-60.381, 18.879, 194.141),
    //   new Vector3(-57.557, 18.888, 195.195),
    //   new Vector3(-54.637, 18.91, 196.385),
    //   new Vector3(-51.641, 18.94, 197.656),
    //   new Vector3(-48.588, 18.972, 198.956),
    //   new Vector3(-45.498, 19.001, 200.233),
    //   new Vector3(-42.39, 19.019, 201.431),
    //   new Vector3(-39.284, 19.022, 202.499),
    //   new Vector3(-36.2, 19.004, 203.382),
    //   new Vector3(-33.158, 18.958, 204.028),
    //   new Vector3(-30.176, 18.879, 204.384),
    //   new Vector3(-27.124, 18.762, 204.453),
    //   new Vector3(-23.909, 18.609, 204.292),
    //   new Vector3(-20.608, 18.428, 203.929),
    //   new Vector3(-17.296, 18.224, 203.391),
    //   new Vector3(-14.051, 18.006, 202.704),
    //   new Vector3(-10.947, 17.78, 201.896),
    //   new Vector3(-8.062, 17.553, 200.994),
    //   new Vector3(-5.472, 17.332, 200.024),
    //   new Vector3(-3.252, 17.124, 199.014),
    //   new Vector3(-1.479, 16.936, 197.992),
    //   new Vector3(-0.2, 16.768, 196.911),
    //   new Vector3(0.638, 16.615, 195.72),
    //   new Vector3(1.109, 16.471, 194.432),
    //   new Vector3(1.287, 16.332, 193.063),
    //   new Vector3(1.244, 16.194, 191.628),
    //   new Vector3(1.055, 16.052, 190.142),
    //   new Vector3(0.792, 15.902, 188.619),
    //   new Vector3(0.531, 15.74, 187.074),
    //   new Vector3(0.344, 15.56, 185.522),
    //   new Vector3(0.304, 15.359, 183.978),
    //   new Vector3(0.336, 15.15, 182.511),
    //   new Vector3(0.32, 14.947, 181.146),
    //   new Vector3(0.266, 14.741, 179.827),
    //   new Vector3(0.185, 14.526, 178.499),
    //   new Vector3(0.087, 14.296, 177.108),
    //   new Vector3(-0.018, 14.043, 175.599),
    //   new Vector3(-0.119, 13.761, 173.918),
    //   new Vector3(-0.206, 13.443, 172.009),
    //   new Vector3(-0.27, 13.083, 169.818),
    //   new Vector3(-0.299, 12.673, 167.29),
    //   new Vector3(-0.294, 12.177, 164.315),
    //   new Vector3(-0.267, 11.582, 160.89),
    //   new Vector3(-0.222, 10.917, 157.117),
    //   new Vector3(-0.165, 10.208, 153.101),
    //   new Vector3(-0.102, 9.484, 148.945),
    //   new Vector3(-0.036, 8.774, 144.752),
    //   new Vector3(0.026, 8.104, 140.626),
    //   new Vector3(0.079, 7.503, 136.671),
    //   new Vector3(0.118, 6.999, 132.991),
    //   new Vector3(0.138, 6.619, 129.688),
    //   new Vector3(0.139, 6.368, 126.717),
    //   new Vector3(0.127, 6.217, 123.945),
    //   new Vector3(0.104, 6.148, 121.344),
    //   new Vector3(0.074, 6.146, 118.889),
    //   new Vector3(0.038, 6.191, 116.552),
    //   new Vector3(0.0, 6.266, 114.307),
    //   new Vector3(-0.038, 6.354, 112.126),
    //   new Vector3(-0.074, 6.437, 109.983),
    //   new Vector3(-0.105, 6.497, 107.851),
    //   new Vector3(-0.129, 6.518, 105.703),
    //   new Vector3(-0.15, 6.514, 103.609),
    //   new Vector3(-0.174, 6.512, 101.632),
    //   new Vector3(-0.2, 6.511, 99.744),
    //   new Vector3(-0.224, 6.511, 97.913),
    //   new Vector3(-0.246, 6.512, 96.109),
    //   new Vector3(-0.263, 6.513, 94.299),
    //   new Vector3(-0.273, 6.515, 92.454),
    //   new Vector3(-0.273, 6.517, 90.543),
    //   new Vector3(-0.263, 6.518, 88.535),
    //   new Vector3(-0.239, 6.518, 86.399),
    //   new Vector3(-0.195, 6.518, 84.155),
    //   new Vector3(-0.127, 6.518, 81.846),
    //   new Vector3(-0.043, 6.518, 79.475),
    //   new Vector3(0.052, 6.518, 77.047),
    //   new Vector3(0.151, 6.518, 74.566),
    //   new Vector3(0.248, 6.518, 72.036),
    //   new Vector3(0.336, 6.518, 69.46),
    //   new Vector3(0.41, 6.518, 66.842),
    //   new Vector3(0.463, 6.518, 64.187),
    //   new Vector3(0.488, 6.518, 61.498),
    //   new Vector3(0.484, 6.518, 58.805),
    //   new Vector3(0.457, 6.518, 56.118),
    //   new Vector3(0.41, 6.518, 53.417),
    //   new Vector3(0.349, 6.518, 50.68),
    //   new Vector3(0.278, 6.518, 47.887),
    //   new Vector3(0.203, 6.518, 45.014),
    //   new Vector3(0.129, 6.518, 42.042),
    //   new Vector3(0.06, 6.518, 38.949),
    //   new Vector3(0.002, 6.518, 35.714),
    //   new Vector3(-0.041, 6.518, 32.315),
    //   new Vector3(-0.046, 6.514, 28.609),
    //   new Vector3(-0.005, 6.502, 24.54),
    //   new Vector3(0.064, 6.486, 20.215),
    //   new Vector3(0.144, 6.469, 15.741),
    //   new Vector3(0.214, 6.454, 11.224),
    //   new Vector3(0.259, 6.444, 6.77),
    //   new Vector3(0.259, 6.442, 2.488),
    //   new Vector3(0.196, 6.452, -1.517),
    //   new Vector3(0.053, 6.476, -5.138),
    //   new Vector3(-0.189, 6.518, -8.268),
    //   new Vector3(-0.513, 6.583, -10.951),
    //   new Vector3(-0.892, 6.67, -13.324),
    //   new Vector3(-1.327, 6.775, -15.416),
    //   new Vector3(-1.818, 6.892, -17.257),
    //   new Vector3(-2.366, 7.015, -18.875),
    //   new Vector3(-2.972, 7.14, -20.3),
    //   new Vector3(-3.636, 7.262, -21.561),
    //   new Vector3(-4.359, 7.374, -22.687),
    //   new Vector3(-5.143, 7.471, -23.707),
    //   new Vector3(-5.987, 7.549, -24.652),
    //   new Vector3(-6.873, 7.605, -25.458),
    //   new Vector3(-7.79, 7.642, -26.059),
    //   new Vector3(-8.75, 7.666, -26.483),
    //   new Vector3(-9.765, 7.681, -26.753),
    //   new Vector3(-10.847, 7.691, -26.896),
    //   new Vector3(-12.008, 7.702, -26.937),
    //   new Vector3(-13.26, 7.718, -26.901),
    //   new Vector3(-14.616, 7.743, -26.815),
    //   new Vector3(-16.088, 7.783, -26.703),
    //   new Vector3(-17.687, 7.842, -26.591),
    //   new Vector3(-19.505, 7.923, -26.356),
    //   new Vector3(-21.579, 8.024, -25.903),
    //   new Vector3(-23.843, 8.14, -25.301),
    //   new Vector3(-26.228, 8.265, -24.616),
    //   new Vector3(-28.668, 8.396, -23.917),
    //   new Vector3(-31.095, 8.526, -23.272),
    //   new Vector3(-33.442, 8.65, -22.748),
    //   new Vector3(-35.643, 8.765, -22.413),
    //   new Vector3(-37.629, 8.864, -22.336),
    //   new Vector3(-39.333, 8.943, -22.583),
    //   new Vector3(-40.778, 9.006, -23.186),
    //   new Vector3(-42.044, 9.06, -24.089),
    //   new Vector3(-43.155, 9.106, -25.236),
    //   new Vector3(-44.133, 9.142, -26.57),
    //   new Vector3(-45.002, 9.171, -28.031),
    //   new Vector3(-45.785, 9.19, -29.563),
    //   new Vector3(-46.505, 9.202, -31.108),
    //   new Vector3(-47.184, 9.205, -32.608),
    //   new Vector3(-47.846, 9.199, -34.006),
    //   new Vector3(-48.513, 9.185, -35.243),
    //   new Vector3(-49.175, 9.162, -36.348),
    //   new Vector3(-49.802, 9.129, -37.397),
    //   new Vector3(-50.388, 9.087, -38.401),
    //   new Vector3(-50.927, 9.036, -39.376),
    //   new Vector3(-51.415, 8.977, -40.334),
    //   new Vector3(-51.846, 8.911, -41.289),
    //   new Vector3(-52.215, 8.838, -42.254),
    //   new Vector3(-52.516, 8.759, -43.244),
    //   new Vector3(-52.743, 8.674, -44.271),
    //   new Vector3(-52.893, 8.584, -45.349),
    //   new Vector3(-52.883, 8.488, -46.469),
    //   new Vector3(-52.677, 8.382, -47.612),
    //   new Vector3(-52.329, 8.269, -48.776),
    //   new Vector3(-51.896, 8.149, -49.959),
    //   new Vector3(-51.435, 8.023, -51.162),
    //   new Vector3(-51.002, 7.893, -52.381),
    //   new Vector3(-50.654, 7.759, -53.617),
    //   new Vector3(-50.445, 7.623, -54.868),
    //   new Vector3(-50.434, 7.485, -56.133),
    //   new Vector3(-50.675, 7.347, -57.409),
    //   new Vector3(-51.203, 7.206, -58.689),
    //   new Vector3(-51.978, 7.059, -59.967),
    //   new Vector3(-52.946, 6.908, -61.251),
    //   new Vector3(-54.054, 6.754, -62.548),
    //   new Vector3(-55.247, 6.597, -63.865),
    //   new Vector3(-56.473, 6.44, -65.209),
    //   new Vector3(-57.678, 6.284, -66.587),
    //   new Vector3(-58.808, 6.129, -68.007),
    //   new Vector3(-59.809, 5.977, -69.475),
    //   new Vector3(-60.627, 5.829, -70.998),
    //   new Vector3(-61.244, 5.687, -72.59),
    //   new Vector3(-61.703, 5.55, -74.25),
    //   new Vector3(-62.047, 5.416, -75.967),
    //   new Vector3(-62.32, 5.284, -77.726),
    //   new Vector3(-62.564, 5.153, -79.516),
    //   new Vector3(-62.823, 5.02, -81.324),
    //   new Vector3(-63.14, 4.884, -83.136),
    //   new Vector3(-63.558, 4.744, -84.941),
    //   new Vector3(-64.12, 4.598, -86.726),
    //   new Vector3(-64.868, 4.445, -88.477),
    //   new Vector3(-65.837, 4.274, -90.182),
    //   new Vector3(-67.0, 4.083, -91.847),
    //   new Vector3(-68.312, 3.879, -93.487),
    //   new Vector3(-69.725, 3.669, -95.119),
    //   new Vector3(-71.193, 3.459, -96.757),
    //   new Vector3(-72.672, 3.257, -98.419),
    //   new Vector3(-74.114, 3.07, -100.119),
    //   new Vector3(-75.473, 2.905, -101.875),
    //   new Vector3(-76.703, 2.769, -103.701),
    //   new Vector3(-77.758, 2.669, -105.613),
    //   new Vector3(-78.611, 2.601, -107.698),
    //   new Vector3(-79.292, 2.556, -109.987),
    //   new Vector3(-79.848, 2.532, -112.414),
    //   new Vector3(-80.322, 2.528, -114.912),
    //   new Vector3(-80.757, 2.542, -117.415),
    //   new Vector3(-81.199, 2.574, -119.858),
    //   new Vector3(-81.692, 2.622, -122.174),
    //   new Vector3(-82.279, 2.686, -124.296),
    //   new Vector3(-83.006, 2.762, -126.16),
    //   new Vector3(-83.915, 2.851, -127.699),
    //   new Vector3(-85.03, 2.964, -128.934),
    //   new Vector3(-86.315, 3.107, -129.948),
    //   new Vector3(-87.732, 3.275, -130.762),
    //   new Vector3(-89.242, 3.46, -131.397),
    //   new Vector3(-90.807, 3.656, -131.876),
    //   new Vector3(-92.386, 3.857, -132.221),
    //   new Vector3(-93.942, 4.054, -132.452),
    //   new Vector3(-95.435, 4.243, -132.594),
    //   new Vector3(-96.826, 4.416, -132.666),
    //   new Vector3(-98.077, 4.566, -132.691),
    //   new Vector3(-99.189, 4.698, -132.644),
    //   new Vector3(-100.204, 4.823, -132.489),
    //   new Vector3(-101.142, 4.94, -132.231),
    //   new Vector3(-102.023, 5.05, -131.872),
    //   new Vector3(-102.869, 5.154, -131.419),
    //   new Vector3(-103.698, 5.251, -130.875),
    //   new Vector3(-104.532, 5.342, -130.244),
    //   new Vector3(-105.392, 5.428, -129.531),
    //   new Vector3(-106.297, 5.508, -128.74),
    //   new Vector3(-107.268, 5.584, -127.875),
    //   new Vector3(-108.387, 5.643, -126.949),
    //   new Vector3(-109.678, 5.679, -125.963),
    //   new Vector3(-111.076, 5.7, -124.906),
    //   new Vector3(-112.513, 5.712, -123.77),
    //   new Vector3(-113.926, 5.724, -122.543),
    //   new Vector3(-115.246, 5.743, -121.215),
    //   new Vector3(-116.41, 5.775, -119.777),
    //   new Vector3(-117.349, 5.829, -118.217),
    //   new Vector3(-118.0, 5.911, -116.527),
    //   new Vector3(-118.295, 6.03, -114.694),
    //   new Vector3(-118.167, 6.195, -112.607),
    //   new Vector3(-117.648, 6.405, -110.213),
    //   new Vector3(-116.818, 6.649, -107.593),
    //   new Vector3(-115.76, 6.916, -104.829),
    //   new Vector3(-114.555, 7.194, -102.0),
    //   new Vector3(-113.284, 7.472, -99.19),
    //   new Vector3(-112.029, 7.74, -96.478),
    //   new Vector3(-110.871, 7.986, -93.947),
    //   new Vector3(-109.891, 8.199, -91.676),
    //   new Vector3(-109.172, 8.367, -89.749),
    //   new Vector3(-108.623, 8.489, -88.234),
    //   new Vector3(-108.111, 8.572, -87.086),
    //   new Vector3(-107.65, 8.627, -86.214),
    //   new Vector3(-107.254, 8.66, -85.526),
    //   new Vector3(-106.937, 8.681, -84.929),
    //   new Vector3(-106.714, 8.698, -84.333),
    //   new Vector3(-106.6, 8.718, -83.644),
    //   new Vector3(-106.608, 8.751, -82.772),
    //   new Vector3(-106.752, 8.804, -81.624),
    //   new Vector3(-107.048, 8.886, -80.108),
    //   new Vector3(-107.565, 9.001, -78.152),
    //   new Vector3(-108.325, 9.143, -75.812),
    //   new Vector3(-109.276, 9.304, -73.187),
    //   new Vector3(-110.363, 9.477, -70.375),
    //   new Vector3(-111.531, 9.655, -67.476),
    //   new Vector3(-112.727, 9.83, -64.589),
    //   new Vector3(-113.895, 9.995, -61.814),
    //   new Vector3(-114.982, 10.142, -59.248),
    //   new Vector3(-115.932, 10.264, -56.992),
    //   new Vector3(-116.693, 10.353, -55.145),
    //   new Vector3(-117.275, 10.41, -53.742),
    //   new Vector3(-117.74, 10.44, -52.702),
    //   new Vector3(-118.112, 10.449, -51.945),
    //   new Vector3(-118.413, 10.442, -51.392),
    //   new Vector3(-118.665, 10.422, -50.963),
    //   new Vector3(-118.892, 10.395, -50.579),
    //   new Vector3(-119.116, 10.365, -50.16),
    //   new Vector3(-119.36, 10.338, -49.628),
    //   new Vector3(-119.647, 10.317, -48.903),
    //   new Vector3(-119.999, 10.307, -47.905),
    //   new Vector3(-120.424, 10.305, -46.59),
    //   new Vector3(-120.901, 10.304, -45.014),
    //   new Vector3(-121.414, 10.304, -43.253),
    //   new Vector3(-121.945, 10.304, -41.379),
    //   new Vector3(-122.477, 10.304, -39.468),
    //   new Vector3(-122.993, 10.305, -37.593),
    //   new Vector3(-123.474, 10.306, -35.828),
    //   new Vector3(-123.904, 10.306, -34.248),
    //   new Vector3(-124.266, 10.307, -32.926),
    //   new Vector3(-124.542, 10.307, -31.936),
    // ];

    const soundConfig: SoundConfig = {
      backgroundMusic: 'sunnySkies',
      backgroundMusicVolume: 1.0,
    };

    const cameraConfig: CameraConfig = {
      alignToPath: false,
      distance: 8,
      heightOffset: 1.2,
      smoothing: 40,
      alphaSmoothing: 1,
      collision: true,
      orthographic: false,
      orthographicZoom: 8,
      orthographicAlpha: Math.PI / 10,
      orthographicBeta: Math.PI / 3,
    };

    const collectibleConfig: CollectibleDistributionConfig = {
      offset: new Vector3(0, 1, 0),
      probability: 0.1,
      minPoint: 10,
      mixPatternTypes: false,
      maxPatternSize: 3,
      spawnRange: 60,
      despawnRange: 90,
    };

    const waterConfig: WaterConfig = {
      level: 0.5,
      depth: 50,
      size: 2000,
      effect: true,
      mesh: true,
      renderTargetSize: new Vector2(256, 256), // reduced from 512 for better performance
      bumpTextureUrl: 'assets/textures/waterbump2.png',
      bumpScale: 10,
      windForce: 1,
      waveHeight: 0.1,
      waveLength: 0.1,
      bumpHeight: 0.1,
      windDirection: new Vector2(1, 1),
      waterColor: new Color3(0, 0, 221 / 255),
      colorBlendFactor: 0.2,
      alpha: 0.95,
      backFaceCulling: true, // water only seen from above → performance gain
    };

    const decorationConfig: DecorationConfig = {
      assets: [
        new Asset('treeA', AssetType.MESH, 'assets/model/forest/', 'Tree_1_A_Color1.gltf'),
        new Asset('treeB', AssetType.MESH, 'assets/model/forest/', 'Tree_1_B_Color1.gltf'),
        new Asset('treeC', AssetType.MESH, 'assets/model/forest/', 'Tree_1_C_Color1.gltf'),
        new Asset('treeD', AssetType.MESH, 'assets/model/forest/', 'Tree_2_A_Color1.gltf'),
        new Asset('treeE', AssetType.MESH, 'assets/model/forest/', 'Tree_2_B_Color1.gltf'),
        new Asset('treeF', AssetType.MESH, 'assets/model/forest/', 'Tree_2_C_Color1.gltf'),
        new Asset('treeG', AssetType.MESH, 'assets/model/forest/', 'Tree_3_A_Color1.gltf'),
        new Asset('treeH', AssetType.MESH, 'assets/model/forest/', 'Tree_3_B_Color1.gltf'),
        new Asset('log', AssetType.MESH, 'assets/model/nature/', 'log.glb'),
        new Asset('rockA', AssetType.MESH, 'assets/model/forest_nature/', 'Rock_3_J_Color1.gltf'),
        new Asset('rockB', AssetType.MESH, 'assets/model/forest_nature/', 'Rock_3_M_Color1.gltf'),
        new Asset('rockC', AssetType.MESH, 'assets/model/forest_nature/', 'Rock_3_N_Color1.gltf'),
        new Asset('flowerYellow', AssetType.MESH, 'assets/model/nature/', 'flower_yellowA.glb'),
        new Asset('flowerRed', AssetType.MESH, 'assets/model/nature/', 'flower_redB.glb'),
        new Asset('flowerPurple', AssetType.MESH, 'assets/model/nature/', 'flower_purpleC.glb'),
        new Asset('cloud', AssetType.MESH, 'assets/model/platformer_cute/', 'cloud.glb'),
      ],
      assetObjects: [
        new AssetObject('treeA').withType('trees').withMetallic(0.1),
        new AssetObject('treeB').withType('trees').withMetallic(0.1),
        new AssetObject('treeC').withType('trees').withMetallic(0.1),
        new AssetObject('treeD').withType('trees').withMetallic(0.1),
        new AssetObject('treeE').withType('trees').withMetallic(0.1),
        new AssetObject('treeF').withType('trees').withMetallic(0.1),
        new AssetObject('treeG').withType('trees').withMetallic(0.1),
        new AssetObject('treeH').withType('trees').withMetallic(0.1),
        new AssetObject('log').withType('trees').withMetallic(0.1),
        new AssetObject('rockA').withType('rocks').withMetallic(0.1),
        new AssetObject('rockB').withType('rocks').withMetallic(0.1),
        new AssetObject('rockC').withType('rocks').withMetallic(0.1),
        new AssetObject('flowerYellow').withType('flowers'),
        new AssetObject('flowerRed').withType('flowers'),
        new AssetObject('flowerPurple').withType('flowers'),
        new AssetObject('cloud').withType('cloud'),
      ],
      distributions: [
        // {
        //   types: ['trees'],
        //   mode: 'zone',
        //   zone: { min: -60, max: 60 },
        //   deadZone: { min: -10, max: 10 },
        //   density: 1,
        //   interval: 2,
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 1, max: 4 },
        //   skipProbability: 0,
        //   skipOccupied: true,
        //   alignToFace: false,
        //   useLevelYPosition: true,
        //   rolling: true,
        // },

        // {
        //   types: ['flowers'],
        //   mode: 'zone',
        //   zone: { min: -60, max: 60 },
        //   deadZone: { min: -10, max: 10 },
        //   density: 6,
        //   interval: 1,
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 2.5, max: 5 },
        //   skipProbability: 0,
        //   skipOccupied: true,
        //   alignToFace: false,
        //   useLevelYPosition: true,
        //   rolling: true,
        // },

        // {
        //   types: ['cloud'],
        //   mode: 'zone',
        //   zone: { min: -500, max: 500 },
        //   density: 2,
        //   interval: 2,
        //   alpha: 0.5,
        //   offset: new Vector3(0, 40, 0),
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 4, max: 12 },
        //   skipProbability: 0,
        //   skipOccupied: false,
        // },

        // {
        //   types: ['rocks'],
        //   mode: 'zone',
        //   zone: { min: -70, max: 70 },
        //   deadZone: { min: -10, max: 10 },
        //   density: 5,
        //   interval: 1,
        //   rotationY: { min: 0, max: Math.PI * 2 },
        //   scale: { min: 0.6, max: 1.2 },
        //   skipProbability: 0.2,
        //   useLevelYPosition: true,
        //   alignToFace: true,
        // },
        {
          types: ['rocks'],
          mode: 'absolute',
          physics: true,
          absolutePlacements: [
            {
              position: new Vector3(24.703, 2.186, -15.757),
              rotation: new Vector3(0.0, 0.0, 0.0),
              scale: new Vector3(1.529, 5.172, 8.604),
            },
          ],
        },
      ],
    };

    const minPoint = 20;

    const obstacleConfig: ObstacleConfig = ObstaclePrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: 10,
      spawnRange: 60,
      despawnRange: 90,
      obstacleColor: 'red',
    });

    const enemyConfig: EnemyConfig = EnemyPrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: 10,
      spawnRange: 60,
      despawnRange: 90,
    });

    const playerConfig: PlayerConfig = {
      startPathIndex: 0,
      startOffset: new Vector3(0, 10, 0),
      autoRun: 0,
      speed: 10,
      jumpSpeed: 11,
      backwardSpeed: 4,
      turnSpeed: 10,
      dashSpeed: 12,
      projectileSpeed: 20,
    };

    const heightMapConfig = HeightMapConfig.builder()
      .name('heightmap-ground')
      .heightMapImageUrl('assets/heightmap/snowdon2.png')
      //.heightMapImageUrl('assets/heightmap/ambientocclusion.jpg')
      .width(2200)
      .height(2200)
      .updatable(true)
      .subdivisions(30)
      .castShadows(false)
      .maxHeight(120)
      .minHeight(0)
      .metadata({
        centerOnPath: false,
      })
      .terrainTextureName1('grass')
      .terrainTextureName2('cracked')
      .terrainTextureName3('cracked')
      .terrainTextureScale(new Vector3(100, 100, 100))
      .backFaceCulling(false)
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, -1, 0))
      .build();

    const atlasConfig = [new TextureAtlas('cartoon1', 'assets/atlas/', 'cartoon1.png', 'cartoon1.json')];

    const spriteConfig = [
      {
        asset: new Asset('palm_cartoon', AssetType.IMAGE, 'assets/sprites/', 'palm_cartoon.png'),
        scale: { min: 5, max: 10 },
        capacity: 5000,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1088,
        cellHeight: 1200,
        name: 'palm_cartoon',
        offsetY: -1,
        yRange: { min: 10, max: 150 },
        deadZone: { min: 500, max: 2500 },
      },
      {
        asset: new Asset('tree_cartoon', AssetType.IMAGE, 'assets/sprites/', 'tree_cartoon.png'),
        scale: { min: 5, max: 10 },
        capacity: 5000,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1136,
        cellHeight: 1200,
        name: 'tree_cartoon',
        offsetY: -1,
        yRange: { min: 10, max: 150 },
        deadZone: { min: 500, max: 2500 },
      },
    ];

    const config = new LevelConfig(
      'Test Level',
      2, // seed
      100, // timelimit
      Level.fullGravity, // gravity
      -45, // killing height
      100, // height above ground
      40, // checkpoint interval
      4, // goal end offset
      playerConfig,
      cameraConfig,
      pathConfig,
      lightConfig,
      skyboxConfig,
      materialConfig
    );

    config.sound = soundConfig;
    //config.fog = fogConfig;
    config.collectible = collectibleConfig;
    config.postProcess = postProcessConfig;
    config.baseScene = baseSceneConfig;
    config.heightMap = heightMapConfig;
    config.decoration = decorationConfig;
    //config.enemy = enemyConfig;
    config.obstacle = obstacleConfig;
    config.water = waterConfig;
    config.atlasses = atlasConfig;
    config.assets = assetConfig;
    config.sprites = spriteConfig;
    return config;
  }
}
